/**
 * ProductFeaturesPage — Products & Features · Product/Feature Matrix.
 *
 * Reads /v3/config/product-features and pivots the junction into a
 * product-keyed view (one row per product, with feature states pivoted
 * into chips). Below the pivot, a flat editable table exposes create /
 * edit / delete on each (productCode, featureCode) mapping.
 *
 * 5-bar critical surface — every editor field is an FK dropdown or enum;
 * no free-text. productCode + featureCode are FK dropdowns from the live
 * /v3/config/products + /v3/config/features catalogs; state is the
 * ProductFeatureState enum. Writes go through the typed
 * client.retail.admin.products.productFeatures.{upsert,remove} surface with a
 * bearer-only (empty bypass key) call — no bypass prompt.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Feature,
  Product,
  ProductFeature,
  ProductFeatureState,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface PivotRow {
  productCode: string;
  required: ReadonlyArray<string>;
  recommended: ReadonlyArray<string>;
  available: ReadonlyArray<string>;
  unavailable: ReadonlyArray<string>;
}

interface EditDraft {
  productCode: string;
  featureCode: string;
  state: ProductFeatureState;
  isActive: boolean;
  /** True when editing an existing row (PK parts locked); false on create. */
  isExisting: boolean;
}

const STATE_OPTIONS: ReadonlyArray<{ value: ProductFeatureState; label: string }> = [
  { value: 'Unavailable', label: 'Unavailable' },
  { value: 'Available', label: 'Available' },
  { value: 'Recommended', label: 'Recommended' },
  { value: 'Required', label: 'Required' },
];

function bucket(state: ProductFeatureState | string | undefined): keyof Omit<PivotRow, 'productCode'> | null {
  switch (state) {
    case 'Required': return 'required';
    case 'Recommended': return 'recommended';
    case 'Available': return 'available';
    case 'Unavailable': return 'unavailable';
    default: return null;
  }
}

function newDraft(): EditDraft {
  return { productCode: '', featureCode: '', state: 'Available', isActive: true, isExisting: false };
}

function rowToDraft(pf: ProductFeature): EditDraft {
  return {
    productCode: pf.productCode,
    featureCode: pf.featureCode,
    state: pf.state,
    isActive: true,
    isExisting: true,
  };
}

export function ProductFeaturesPage(): JSX.Element {
  const client = useApiClient();
  const [mappings, setMappings] = useState<ReadonlyArray<ProductFeature> | null>(null);
  const [products, setProducts] = useState<ReadonlyArray<Product>>([]);
  const [features, setFeatures] = useState<ReadonlyArray<Feature>>([]);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [pfResp, prodResp, featResp] = await Promise.all([
        client.retail.config.productFeatures(),
        client.retail.config.products(),
        client.retail.config.features(),
      ]);
      if (!pfResp.success || !Array.isArray(pfResp.data)) {
        setError(pfResp.error ?? 'Failed to load product features.');
        return;
      }
      setMappings(pfResp.data as ReadonlyArray<ProductFeature>);
      if (prodResp.success && Array.isArray(prodResp.data)) {
        setProducts(prodResp.data as ReadonlyArray<Product>);
      }
      if (featResp.success && Array.isArray(featResp.data)) {
        setFeatures(featResp.data as ReadonlyArray<Feature>);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ─── Read · pivot view ──────────────────────────────────────────────────
  const pivotRows = useMemo<ReadonlyArray<PivotRow>>(() => {
    if (!mappings) return [];
    const byProduct = new Map<string, PivotRow>();
    for (const pf of mappings) {
      let r = byProduct.get(pf.productCode);
      if (!r) {
        r = { productCode: pf.productCode, required: [], recommended: [], available: [], unavailable: [] };
        byProduct.set(pf.productCode, r);
      }
      const b = bucket(pf.state);
      if (b) (r[b] as string[]).push(pf.featureCode);
    }
    return Array.from(byProduct.values()).sort((a, b) => a.productCode.localeCompare(b.productCode));
  }, [mappings]);

  const pivotColumns: ReadonlyArray<DataTableColumn<PivotRow>> = useMemo(() => [
    {
      header: 'Product',
      cell: (r) => <code className="cell-mono--bold">{r.productCode}</code>,
    },
    {
      header: 'Required',
      cell: (r) => (
        <div className="pill-stack">
          {r.required.length === 0 ? <span className="cell-dim">—</span> : r.required.map((c) => (
            <Pill key={c} tone="err">{c}</Pill>
          ))}
        </div>
      ),
    },
    {
      header: 'Recommended',
      cell: (r) => (
        <div className="pill-stack">
          {r.recommended.length === 0 ? <span className="cell-dim">—</span> : r.recommended.map((c) => (
            <Pill key={c} tone="warn">{c}</Pill>
          ))}
        </div>
      ),
    },
    {
      header: 'Available',
      cell: (r) => (
        <div className="pill-stack">
          {r.available.length === 0 ? <span className="cell-dim">—</span> : r.available.map((c) => (
            <Pill key={c} tone="info">{c}</Pill>
          ))}
        </div>
      ),
    },
    {
      header: 'Unavailable',
      cell: (r) => (
        <div className="pill-stack">
          {r.unavailable.length === 0 ? <span className="cell-dim">—</span> : r.unavailable.map((c) => (
            <Pill key={c} tone="muted">{c}</Pill>
          ))}
        </div>
      ),
    },
  ], []);

  // ─── Edit · flat mapping table ──────────────────────────────────────────
  const flatRows = useMemo<ReadonlyArray<ProductFeature>>(() => {
    if (!mappings) return [];
    return [...mappings].sort(
      (a, b) =>
        a.productCode.localeCompare(b.productCode) || a.featureCode.localeCompare(b.featureCode),
    );
  }, [mappings]);

  const stateTone = (s: ProductFeatureState): 'err' | 'warn' | 'info' | 'muted' => {
    switch (s) {
      case 'Required': return 'err';
      case 'Recommended': return 'warn';
      case 'Available': return 'info';
      default: return 'muted';
    }
  };

  const remove = useCallback(
    async (pf: ProductFeature): Promise<void> => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete mapping ${pf.productCode} → ${pf.featureCode}?`)) return;
      setError(null);
      try {
        const resp = await client.retail.admin.products.productFeatures.remove(
          '',
          pf.productCode,
          pf.featureCode,
        );
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const flatColumns: ReadonlyArray<DataTableColumn<ProductFeature>> = useMemo(() => [
    { header: 'Product', cell: (r) => <code className="cell-mono--bold">{r.productCode}</code> },
    { header: 'Feature', cell: (r) => <code className="cell-mono">{r.featureCode}</code> },
    { header: 'State', cell: (r) => <Pill tone={stateTone(r.state)}>{r.state}</Pill> },
    {
      header: '',
      cell: (r) => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void remove(r)}>
            Delete
          </Button>
        </div>
      ),
    },
  ], [remove]);

  // ─── Write · save ───────────────────────────────────────────────────────
  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      if (!d.productCode || !d.featureCode) {
        setSaveErr('Select both a product and a feature.');
        return;
      }
      setSaving(true);
      setSaveErr(null);
      try {
        const resp = await client.retail.admin.products.productFeatures.upsert('', {
          productCode: d.productCode,
          featureCode: d.featureCode,
          state: d.state,
          isActive: d.isActive,
        });
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  const productOptions = useMemo(
    () =>
      [...products]
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` })),
    [products],
  );
  const featureOptions = useMemo(
    () =>
      [...features]
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((f) => ({ value: f.code, label: `${f.code} — ${f.displayName}` })),
    [features],
  );

  if (error && !mappings) return <Banner tone="err">Could not load product features: {error}</Banner>;
  if (!mappings) return <Spinner label="Loading product/feature matrix…" />;

  return (
    <>
      {error && <Banner tone="err">{error}</Banner>}

      <Card title={`Product / Feature Matrix — ${pivotRows.length} products`}>
        <DataTable<PivotRow>
          columns={pivotColumns}
          rows={pivotRows}
          rowKey={(r) => r.productCode}
          emptyState={<p className="empty">No product/feature mappings defined.</p>}
        />
      </Card>

      <Card
        title={`Mappings — ${flatRows.length}`}
        actions={
          <Button size="sm" onClick={() => setDraft(newDraft())}>
            New mapping
          </Button>
        }
      >
        <DataTable<ProductFeature>
          columns={flatColumns}
          rows={flatRows}
          rowKey={(r) => `${r.productCode}::${r.featureCode}`}
          emptyState={<p className="empty">No product/feature mappings defined.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isExisting
              ? `Edit mapping — ${draft.productCode} → ${draft.featureCode}`
              : 'New product / feature mapping'
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={520}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Product"
              value={draft.productCode}
              onChange={(v) => setDraft({ ...draft, productCode: v })}
              options={productOptions}
              placeholder="Select a product…"
              disabled={draft.isExisting}
              required
              hint={draft.isExisting ? 'Locked — part of the mapping key.' : 'FK to the products catalog.'}
            />
            <SelectField
              label="Feature"
              value={draft.featureCode}
              onChange={(v) => setDraft({ ...draft, featureCode: v })}
              options={featureOptions}
              placeholder="Select a feature…"
              disabled={draft.isExisting}
              required
              hint={draft.isExisting ? 'Locked — part of the mapping key.' : 'FK to the features catalog.'}
            />
            <SelectField
              label="State"
              value={draft.state}
              onChange={(v) => setDraft({ ...draft, state: v as ProductFeatureState })}
              options={STATE_OPTIONS}
              required
              hint="Eligibility of this feature on the selected product."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
